import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import { HallUser, SpeakingRole, TranscriptsByStep, Turn, TurnTranscript } from '@db8/shared'

pdfMake.vfs = pdfFonts.vfs

interface DebatePDFProps {
  topic: string
  affirmingTeam?: HallUser[]
  dissentingTeam?: HallUser[]
  transcriptions: TranscriptsByStep
  analysis?: any
  ballots?: any[]
  turns: Turn[]
  flowNotes?: any // pages array from FlowNotesProvider
}

export function generateDebatePDF({
  topic,
  affirmingTeam = [],
  dissentingTeam = [],
  transcriptions,
  analysis = '',
  ballots = [],
  turns = [],
  flowNotes = [],
}: DebatePDFProps) {
  // 1) Flatten & sort transcripts
  const allTranscripts: TurnTranscript[] = Object.values(transcriptions)
    .flat()
    .sort((a, b) => (a.turnStep ?? 0) - (b.turnStep ?? 0))

  // 2) Group by turn.type
  const groupedByTurnType = allTranscripts.reduce<Record<string, TurnTranscript[]>>((acc, item) => {
    const roundType = turns[item.turnStep].title || 'Unknown Round'
    if (!acc[roundType]) acc[roundType] = []
    acc[roundType].push(item)
    return acc
  }, {})

  // We'll build a pdfmake docDefinition "content" array
  const content: any[] = []

  // --- Debate Topic ---
  content.push(
    { text: 'Debate Topic:', style: 'header' },
    { text: topic, style: 'subheader', margin: [0, 0, 0, 10] },
  )

  // --- Teams ---
  // Affirming
  content.push({ text: 'Affirming Team:', style: 'header2' })
  affirmingTeam.forEach((user, index) => {
    content.push({
      text: `${index + 1}. ${user.displayName}`,
      margin: [0, 0, 0, 5],
    })
  })
  content.push({ text: '', margin: [0, 0, 0, 10] })

  // Dissenting
  content.push({ text: 'Dissenting Team:', style: 'header2' })
  dissentingTeam.forEach((user, index) => {
    content.push({
      text: `${index + 1}. ${user.displayName}`,
      margin: [0, 0, 0, 5],
    })
  })
  content.push({ text: '', margin: [0, 0, 0, 12] })

  // --- Flow Notes ---
  if (Array.isArray(flowNotes) && flowNotes.length > 0) {
    content.push({ text: 'Flow Notes:', style: 'header', margin: [0, 0, 0, 8] })

    // Each page as a section
    flowNotes.forEach((page: any, pIdx: number) => {
      content.push({ text: page.title || `Page ${pIdx + 1}`, style: 'header2', margin: [0, 4, 0, 6] })

      // Build a table where each debate turn becomes a column with that column's rows
      const visibleColumns = (page.columns || []).filter((c: any) =>
        typeof c?.id === 'number' && turns[c.id] && turns[c.id].title,
      )

      // Table header row: turn abbreviations
      const tableBody: any[] = []
      tableBody.push(
        visibleColumns.map((c: any) => ({
          text: turns[c.id]?.abbreviation || turns[c.id]?.title || String(c.id),
          style: 'roundHeader',
          margin: [0, 0, 0, 4],
        })),
      )

      // Determine the max number of rows across columns to balance the table
      const maxRows = Math.max(
        0,
        ...visibleColumns.map((c: any) => (Array.isArray(c.rows) ? c.rows.length : 0)),
      )

      for (let i = 0; i < maxRows; i++) {
        const rowCells = visibleColumns.map((c: any) => {
          const row = (c.rows || [])[i]
          if (!row) return { text: '', margin: [2, 2, 2, 2] }
          const title = row.title ? `${row.title}` : ''
          const contentText = row.content || ''
          const combined = title && contentText ? `${title}: ${contentText}` : (title || contentText)
          return {
            text: combined,
            style: 'bodyText',
            margin: [2, 2, 2, 4],
          }
        })
        tableBody.push(rowCells)
      }

      content.push({
        table: {
          headerRows: 1,
          widths: visibleColumns.map(() => '*'),
          body: tableBody,
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? '#f2f2f2' : null),
          hLineColor: () => '#e5e5e5',
          vLineColor: () => '#e5e5e5',
        },
        margin: [0, 0, 0, 12],
      })
    })
  }

  // --- Transcripts ---
  content.push({ text: 'Transcripts:', style: 'header', margin: [0, 0, 0, 10] })

  // A small helper to return a "boxed" transcript with alignment & color
  function transcriptBlock(transcript: TurnTranscript) {
    const speakerName = transcript.hallUser?.displayName || 'Unknown Speaker'
    const speechText = transcript.text || ''
    const role = transcript.hallUser?.speakingRole

    // Choose a border color & alignment based on role
    let borderColor = '#000000'
    let alignment: 'left' | 'right' | 'center' = 'left'

    if (role === SpeakingRole.Affirmative) {
      borderColor = 'green'
      alignment = 'left'
    } else if (role === SpeakingRole.Negative) {
      borderColor = 'red'
      alignment = 'right'
    } else if (role === SpeakingRole.Moderator) {
      borderColor = 'gray'
      alignment = 'center'
    }

    return {
      table: {
        widths: ['*'], // single column, full available width
        body: [
          [
            {
              text: [{ text: speakerName + '\n', bold: true }, speechText],
              margin: [5, 5, 5, 5], // padding inside the box
              alignment, // key for left/right/center text alignment
            },
          ],
        ],
      },
      layout: {
        // color the box edges
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
      },
      margin: [0, 0, 0, 10], // space after each transcript block
    }
  }

  // Print each group of transcripts
  Object.entries(groupedByTurnType).forEach(([roundType, items]) => {
    // Round type heading
    content.push({
      text: roundType,
      style: 'roundHeader',
      margin: [0, 0, 0, 6],
    })

    // Then each transcript block
    items.forEach((transcript) => {
      content.push(transcriptBlock(transcript))
    })
  })

  // --- AI Analysis ---
  // --- Results Section ---
  if (analysis) {
    if (typeof analysis === 'string') {
      // AI Analysis
      content.push({
        text: 'AI Analysis:',
        style: 'header',
        margin: [0, 10, 0, 5],
      })
      content.push({ text: analysis, style: 'bodyText' })
    } else if (analysis.type === 'SIMPLEPOLL') {
      content.push({
        text: 'Poll Results:',
        style: 'header',
        margin: [0, 10, 0, 5],
      })
      content.push({
        text: `${analysis.affirmativeVotes} vote(s) for Affirming, ${analysis.negativeVotes} vote(s) for Dissenting`,
        style: 'bodyText',
        margin: [0, 0, 0, 8],
      })

      if (analysis.voters) {
        const affVoters = analysis.voters[SpeakingRole.Affirmative]?.join(', ') || 'None'
        const negVoters = analysis.voters[SpeakingRole.Negative]?.join(', ') || 'None'

        content.push({
          ul: [
            { text: `Affirming Voters: ${affVoters}`, style: 'bodyText' },
            { text: `Dissenting Voters: ${negVoters}`, style: 'bodyText' },
          ],
          margin: [0, 0, 0, 8],
        })
      }
    } else if (analysis.type === 'EVALUATIONPOLL') {
      content.push({
        text: 'Poll Results:',
        style: 'header',
        margin: [0, 10, 0, 5],
      })

      const criteria = analysis.criteria || []
      const affScores = analysis.scores?.[SpeakingRole.Affirmative] || []
      const negScores = analysis.scores?.[SpeakingRole.Negative] || []

      // Build a table with criteria and scores
      const tableBody = [
        [
          { text: 'Criterion', bold: true },
          { text: 'Affirming Score', bold: true },
          { text: 'Dissenting Score', bold: true },
        ],
      ]

      criteria.forEach((criterion: string, index: number) => {
        tableBody.push([criterion, affScores[index] ?? '-', negScores[index] ?? '-'])
      })

      content.push({
        table: {
          widths: ['*', 'auto', 'auto'],
          body: tableBody,
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 8],
      })
    }
  }

  // --- Ballots Section ---
  if (ballots?.length > 0) {
    content.push({
      text: 'Ballot Results:',
      style: 'header',
      margin: [0, 10, 0, 6],
    })

    ballots.forEach((ballot) => {
      const decisionColor =
        ballot.decision === 'AFFIRMATIVE'
          ? 'green'
          : ballot.decision === 'NEGATIVE'
            ? 'blue'
            : 'black'

      const judgeName =
        ballot.user?.username ||
        ballot.user?.displayName ||
        ballot.user?.email?.split('@')[0] ||
        'Anonymous'

      const feedbackRows = ballot.feedbacks?.sort((a, b) => a.rank - b.rank) || []

      content.push(
        {
          text: `Judge: ${judgeName}`,
          style: 'subheader',
          margin: [0, 5, 0, 2],
        },
        {
          text: `${ballot.decision} Wins`,
          color: decisionColor,
          bold: true,
          margin: [0, 0, 0, 2],
        },
        {
          text: `Low-Point Win: ${ballot.lowPointWin ? 'Yes' : 'No'}`,
          margin: [0, 0, 0, 2],
        },
        {
          text: `Reason: ${ballot.reason}`,
          margin: [0, 0, 0, 2],
        },
        {
          text: `Comments: ${ballot.comments}`,
          margin: [0, 0, 0, 6],
        },
      )

      feedbackRows.forEach((fb) => {
        const speakerName =
          affirmingTeam.concat(dissentingTeam).find((u) => u.id === fb.userId)?.displayName ||
          'Unknown Speaker'

        const text = `• ${speakerName}: ${fb.points} pts | Rank #${fb.rank}${
          fb.content ? ` — "${fb.content}"` : ''
        }`

        content.push({
          text,
          fontSize: 11,
          margin: [0, 0, 0, 3],
        })
      })

      content.push({ text: '', margin: [0, 0, 0, 10] })
    })
  }

  // 3) Build docDefinition
  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 50, 40, 50],
    content,
    styles: {
      header: {
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 8],
      },
      subheader: {
        fontSize: 14,
        margin: [0, 0, 0, 8],
      },
      header2: {
        fontSize: 14,
        bold: true,
        margin: [0, 0, 0, 6],
      },
      roundHeader: {
        fontSize: 13,
        bold: true,
      },
      bodyText: {
        fontSize: 12,
      },
    },
  }

  // 4) Create & download
  pdfMake.createPdf(docDefinition).download(`${topic.replace(/\s+/g, '_')}_debate.pdf`)
}
